// import { Box, Button, Grid, TextField, Typography, useTheme } from "@material-ui/core";
import { Box, Grid, TextField, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { Prompt, useHistory } from 'react-router';

import { CREATE, DataAPIContext } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import PageMeta from '../components/PageMeta';
import UploadImageButton from '../components/UploadImageButton';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(8),
    },
    gridRoot: {
        marginLeft: theme.spacing(1),
    },
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
}));

const validateInput = (filter, preSave) => {
    const errors = {};
    let canSave = true;
    if (!filter.name || filter.name.length <= 0) {
        if (filter._changed && filter._changed_name) {
            errors.name = { error: true, helperText: 'Filter name is required' };
        }
        canSave = false;
    }
    if (!filter.image) {
        if (filter._changed && filter._changed_file) {
            errors.image = { error: true, helperText: 'Image is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

const FilterCreate = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm, currentLicensee } = React.useContext(ContextStore);
    const resource = 'trip_type';
    const history = useHistory();
    const classes = useStyles();
    const saveRef = React.useRef(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [saveError, setSaveError] = React.useState(false);
    const [, setServerErrors] = React.useState([]);
    const [filter, setFilter] = React.useState({
        name: '',
        description: '',
        image: '',
        tour_operator: currentLicensee || null,
        _changed: false,
    });
    const { enqueueSnackbar } = useSnackbar();
    const { canSave, errors } = validateInput(filter, saveError);

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            console.log('onDrop', acceptedFiles, filter);
            setFilter({ ...filter, image: acceptedFiles[0], _changed: true, _changed_file: true });
            setSaveError(false);
        },
        [filter]
    );

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });

    const handleChange = (ev) => {
        const cfn = `_changed_${ev.target.name}`;
        setFilter({ ...filter, [ev.target.name]: ev.target.value, _changed: true, [cfn]: true });
        setSaveError(false);
    };
    const onAccessTagsChange = (ev, atags, reason) => {
        setFilter((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };
    const handleSave = (ev) => {
        console.log('save', filter);
        const { canSave } = validateInput(filter, true);
        if (!canSave) {
            setSaveError(true);
            return;
        }
        setSyncActive(true);
        disableMuiButton(saveRef.current);
        (async () => {
            try {
                const res = await dataAPI(CREATE, resource, { data: filter, upload: true });
                console.log('create', res);
                if (res.statusCode === 201) {
                    enqueueSnackbar('Filter created', { variant: 'success' });
                    setFilter({ ...filter, _changed: false, _changed_name: false, _changed_file: false });
                    history.push(`/explore/filters`);
                } else if (res.statusCode === 406) {
                    enqueueSnackbar('Filter validation error', { variant: 'warning' });
                    console.log('validation errors', res.data);
                    setServerErrors(res.data.errors);
                }
            } finally {
                setSyncActive(false);
                if (saveRef && saveRef.current) {
                    enableMuiButton(saveRef.current);
                }
            }
        })();
    };

    const handleDelete = (ev) => {
        history.push('/explore/filters'); // just go back
    };

    if (!hasPerm('trip_type', 'write')) {
        <AccessDeniedView />;
    }

    return (
        <div className={classes.root}>
            <PageMeta title="Create New Filter" />
            <Prompt when={!!filter._changed} message="Are you sure you want to leave without saving changes?" />
            <FixedLinearProgress visibility={syncActive} />
            <FormLicenseeContext.Provider value={filter.tour_operator}>
            <div className={classes.gridRoot}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Create New Filter</Typography>
                    </Grid>
                    <Grid item xs={12} container justifyContent="center" alignItems="center">
                        <input {...dz.getInputProps()} />
                        <UploadImageButton {...errors.image} imageAction="below" onClick={dz.open} isUpload={true} image={filter.image} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            helperText={`${20 - filter.name.length} characters left`}
                            {...errors.name}
                            required
                            variant="filled"
                            size="small"
                            fullWidth
                            name="name"
                            label="Name"
                            value={filter.name}
                            onChange={handleChange}
                            inputProps={{
                                maxLength: 20,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            helperText={`${520 - filter.description.length} characters left`}
                            {...errors.description}
                            variant="filled"
                            multiline
                            size="small"
                            fullWidth
                            name="description"
                            label="Description"
                            value={filter.description}
                            onChange={handleChange}
                            inputProps={{
                                maxLength: 520,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={2} ml={0}>
                            <TextField
                                helperText={`${128 - (filter.custom_id?.length || 0)} characters left`}
                                variant="filled"
                                size="small"
                                name="custom_id"
                                label="External ID"
                                fullWidth
                                value={filter.custom_id}
                                onChange={handleChange}
                                inputProps={{
                                    maxLength: 128,
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <AccessTagsDropdown value={filter.atag} onChange={onAccessTagsChange} />
                    </Grid>
                </Grid>
            </div>
            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    submitRef={saveRef}
                    onSubmit={handleSave}
                    submitDisabled={!canSave}
                    onCancel={handleDelete}
                    cancelDisabled={syncActive}
                />
            </Box>
            </FormLicenseeContext.Provider>
        </div>
    );
};

export default FilterCreate;
